import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';

export default function TermsOfService() {
  return (
    <Layout>
      <SEO
        title="Terms of Service - B2B CSV"
        description="B2B CSV Terms of Service. Read our terms and conditions for using our business lead generation platform."
        canonical="/terms"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-slate-100 mb-4">Terms of Service</h1>
        <p className="text-slate-400 mb-8">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <div className="space-y-8 text-slate-300">
          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">1. Agreement to Terms</h2>
            <p>
              By accessing or using B2B CSV ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service. We reserve the right to modify these Terms at any time, and your continued use constitutes acceptance of any changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">2. Description of Service</h2>
            <p className="mb-3">
              B2B CSV is an AI-powered business search and lead generation platform that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Searches for businesses using Google Gemini AI and Google Places API</li>
              <li>Enriches business data with contact information and social media profiles</li>
              <li>Exports search results to CSV format</li>
              <li>Provides route optimization for field sales</li>
              <li>Allows saving and combining search results</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">3. Account Registration and Security</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">3.1 Account Creation</h3>
            <p className="mb-3">To use certain features, you must create an account. You agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">3.2 Age Requirement</h3>
            <p>You must be at least 18 years old to use the Service. By using the Service, you represent that you meet this requirement.</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">3.3 Business Use</h3>
            <p>Our Service is intended for legitimate business purposes only. You must not use the Service for personal or consumer purposes unrelated to business operations.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">4. Subscription Plans and Billing</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">4.1 Subscription Tiers</h3>
            <p className="mb-3">We offer the following subscription plans:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Free:</strong> Limited searches and features</li>
              <li><strong>Starter:</strong> $24.99/month - Increased limits</li>
              <li><strong>Pro:</strong> $59.99/month - Advanced features and higher limits</li>
              <li><strong>Enterprise:</strong> Custom pricing - Unlimited usage</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">4.2 Payment</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Subscriptions are billed monthly in advance</li>
              <li>Payments are processed securely through Stripe</li>
              <li>You authorize us to charge your payment method automatically</li>
              <li>All fees are non-refundable except as required by law</li>
              <li>Prices may change with 30 days' notice</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">4.3 Cancellation</h3>
            <p>
              You may cancel your subscription at any time. Cancellations take effect at the end of the current billing period. You will retain access until the period expires. No refunds are provided for partial months.
            </p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">4.4 Free Trial</h3>
            <p>
              If we offer a free trial, you may cancel before the trial ends to avoid charges. If you do not cancel, your payment method will be charged the subscription fee.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">5. Acceptable Use Policy</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">5.1 Permitted Use</h3>
            <p className="mb-3">You may use the Service for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Legitimate business research and lead generation</li>
              <li>Market analysis and competitive research</li>
              <li>Sales prospecting and outreach planning</li>
              <li>Location-based business discovery</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">5.2 Prohibited Use</h3>
            <p className="mb-3">You must NOT use the Service to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Send unsolicited spam or bulk emails</li>
              <li>Scrape or harvest data for resale</li>
              <li>Harass, stalk, or threaten individuals or businesses</li>
              <li>Violate any laws or regulations (including CAN-SPAM, GDPR, CCPA)</li>
              <li>Circumvent usage limits or access controls</li>
              <li>Share your account with others</li>
              <li>Reverse engineer or copy the Service</li>
              <li>Distribute malware or harmful code</li>
              <li>Impersonate others or provide false information</li>
              <li>Interfere with the Service's operation</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">5.3 Rate Limiting</h3>
            <p>
              We implement rate limits to ensure fair use. Excessive use may result in temporary or permanent suspension. Enterprise plans have higher or no rate limits.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">6. Data and Privacy</h2>
            <p className="mb-3">
              Your use of the Service is also governed by our Privacy Policy. Key points:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We collect and process your data as described in our Privacy Policy</li>
              <li>Business data from searches is sourced from public APIs (Google Places)</li>
              <li>You are responsible for complying with data protection laws when using exported data</li>
              <li>We do not sell your personal information</li>
              <li>You own your saved searches and exported data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">7. Intellectual Property</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">7.1 Our Rights</h3>
            <p className="mb-3">
              The Service, including its code, design, branding, and content, is protected by copyright, trademark, and other intellectual property laws. You may not:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Copy, modify, or distribute the Service</li>
              <li>Use our trademarks or branding without permission</li>
              <li>Create derivative works</li>
              <li>Remove copyright or proprietary notices</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">7.2 Your Rights</h3>
            <p>
              You retain ownership of your search queries and saved search data. By using the Service, you grant us a license to process your data solely to provide the Service.
            </p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">7.3 Third-Party Data</h3>
            <p>
              Business data from Google Places API is subject to Google's terms. You agree to comply with Google's terms when using this data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">8. Service Availability and Modifications</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">8.1 Availability</h3>
            <p>
              We strive for high availability but do not guarantee uninterrupted access. The Service may be unavailable due to maintenance, updates, or technical issues.
            </p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">8.2 Modifications</h3>
            <p>
              We may modify, suspend, or discontinue any part of the Service at any time with or without notice. We are not liable for any modifications or discontinuation.
            </p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">8.3 API Changes</h3>
            <p>
              Our Service depends on third-party APIs (Google Gemini, Google Places). Changes to these APIs may affect functionality, search quality, or availability.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">9. Disclaimers and Limitations of Liability</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">9.1 Data Accuracy</h3>
            <p>
              Business data is sourced from third-party APIs and may be inaccurate, incomplete, or outdated. We do not guarantee the accuracy, completeness, or reliability of any data. You are responsible for verifying information before use.
            </p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">9.2 "AS IS" Disclaimer</h3>
            <p className="uppercase font-semibold mb-3">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">9.3 Limitation of Liability</h3>
            <p className="uppercase font-semibold mb-3">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="uppercase font-semibold">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM, OR $100, WHICHEVER IS GREATER.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless B2B CSV, its affiliates, and their officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorney fees) arising from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your violation of applicable laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">11. Termination</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">11.1 By You</h3>
            <p>You may cancel your account at any time through your account settings or by contacting support.</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">11.2 By Us</h3>
            <p className="mb-3">We may suspend or terminate your account immediately if you:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violate these Terms or our Acceptable Use Policy</li>
              <li>Fail to pay subscription fees</li>
              <li>Engage in fraudulent or illegal activity</li>
              <li>Abuse the Service or harm other users</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">11.3 Effect of Termination</h3>
            <p>
              Upon termination, your access to the Service ends immediately. Saved searches may be deleted after 30 days. Fees paid are non-refundable. Sections of these Terms that should survive termination (including disclaimers and limitations of liability) will continue to apply.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">12. Dispute Resolution</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">12.1 Governing Law</h3>
            <p>These Terms are governed by the laws of the United States and the State of Delaware, without regard to conflict of law principles.</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">12.2 Arbitration</h3>
            <p>
              Any dispute arising from these Terms or the Service shall be resolved through binding arbitration, except that either party may seek injunctive relief in court. You waive the right to a jury trial or class action.
            </p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">12.3 Exceptions</h3>
            <p>Small claims court disputes and intellectual property disputes may be brought in court.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">13. General Provisions</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">13.1 Entire Agreement</h3>
            <p>These Terms, along with our Privacy Policy, constitute the entire agreement between you and B2B CSV.</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">13.2 Severability</h3>
            <p>If any provision is found unenforceable, the remaining provisions remain in effect.</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">13.3 Waiver</h3>
            <p>Our failure to enforce any right or provision does not constitute a waiver.</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">13.4 Assignment</h3>
            <p>You may not assign these Terms. We may assign our rights to any affiliate or successor.</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">13.5 Force Majeure</h3>
            <p>We are not liable for delays or failures due to circumstances beyond our reasonable control.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">14. Changes to Terms</h2>
            <p>
              We may update these Terms at any time. Material changes will be notified via email or service notification. Your continued use after changes constitutes acceptance. If you do not agree to changes, you must stop using the Service and cancel your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">15. Contact Information</h2>
            <p className="mb-3">For questions about these Terms, please contact us:</p>
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <p><strong>Email:</strong> legal@b2bcsv.com</p>
              <p><strong>Support:</strong> support@b2bcsv.com</p>
            </div>
          </section>

          <section className="mt-8 p-4 bg-slate-800 border border-slate-700 rounded-lg">
            <p className="text-sm text-slate-400">
              By using B2B CSV, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
}
